// Display-only helper for caste/category admission fee concessions.
//
// The backend is the source of truth for the actual charged amount. This helper
// mirrors that math for UI display using the concession config + defaults that
// the program endpoint returns, so no rule is hardcoded here.

const FALLBACK_DEFAULT_PAYABLE = {
  sc: 0,
  st: 0,
  obc: 50,
  general: 100,
  other: 100,
};

const normalizeCaste = (caste) =>
  typeof caste === "string" ? caste.trim().toLowerCase() : "";

/**
 * Resolve payable percentage from a program payload returned by getProgramByName.
 * @param {object} program - { categoryFeeConcessions, defaultCategoryPayable }
 * @param {string} caste
 * @returns {number} payable percentage [0, 100]
 */
export const getPayablePercentage = (program, caste) => {
  const key = normalizeCaste(caste);
  const defaults = program?.defaultCategoryPayable || FALLBACK_DEFAULT_PAYABLE;

  const override = program?.categoryFeeConcessions?.[key];
  if (override !== null && override !== undefined && override !== "" && !Number.isNaN(Number(override))) {
    return Math.min(100, Math.max(0, Number(override)));
  }
  if (key in defaults) return defaults[key];
  return 100;
};

/**
 * Compute display amounts for the admission fee given a program payload + caste.
 * @param {object} program - includes applicationFee, developmentFee, concessions
 * @param {string} caste
 * @returns {{originalAmount:number, payableAmount:number, discountPercentage:number, isFree:boolean}}
 */
export const getAdmissionFeeDisplay = (program, caste) => {
  const originalAmount =
    (Number(program?.applicationFee) || 0) +
    (Number(program?.developmentFee) || 0);
  const payablePercentage = getPayablePercentage(program, caste);
  const payableAmount = Math.round((originalAmount * payablePercentage) / 100);
  return {
    originalAmount,
    payableAmount,
    discountPercentage: 100 - payablePercentage,
    isFree: payableAmount <= 0,
  };
};

// Mirrors backend utils/categoryFee.js isTuitionConcessionEligible. FY is excluded
// because a first-year's concession is granted on the ADMISSION fee; applying it to FY
// tuition too would discount the same student twice. Unknown/null stage => not
// eligible (legacy sections with no structured yearStage charge full).
const TUITION_CONCESSION_STAGES = ["SY", "TY", "IV", "V"];

/**
 * Is a tuition concession applicable at this year stage?
 * @param {string|null} yearStage - FY|SY|TY|IV|V
 * @returns {boolean}
 */
export const isTuitionConcessionEligible = (yearStage) =>
  TUITION_CONCESSION_STAGES.includes(
    typeof yearStage === "string" ? yearStage.trim().toUpperCase() : "",
  );

/**
 * Compute display amounts for a TUITION fee from the fee structure's own amounts
 * (tuitionFee + developmentFee) — NOT the program's admission fee. Returns a 0%
 * discount when the stage is ineligible, so callers can render the same shape either
 * way.
 *
 * @param {object} program - concession config source (categoryFeeConcessions, defaults)
 * @param {string} caste
 * @param {number} tuitionBase - fees.tuitionFee + fees.developmentFee
 * @param {string|null} yearStage
 * @returns {{originalAmount:number, payableAmount:number, discountPercentage:number, isFree:boolean, applicable:boolean}}
 */
export const getTuitionFeeDisplay = (program, caste, tuitionBase, yearStage) => {
  const originalAmount = Number(tuitionBase) || 0;
  const applicable = isTuitionConcessionEligible(yearStage);
  const payablePercentage = applicable ? getPayablePercentage(program, caste) : 100;
  const payableAmount = Math.round((originalAmount * payablePercentage) / 100);
  return {
    originalAmount,
    payableAmount,
    discountPercentage: 100 - payablePercentage,
    isFree: applicable && payableAmount <= 0,
    applicable,
  };
};
